import React, {FC, useState, useEffect} from "react"
import {Row} from "@irati/ui/src/Layout"
import moment from "moment"

import theme from "@irati/ui/src/global/theme"
import Flex from "@irati/ui/src/Flex"
import {H2, Body} from "@irati/ui/src/Typography"
import FormattedMessage, {InjectIntl, injectIntl} from "@irati/utils/lang"
import {BusinessOverviewDuration, PaymentCurrencyType} from "@irati/types"
import Loading from "@irati/ui/src/Loading"
import adminReportingService from "@irati/adminReporting/services/adminReporting"
import authentication from "@irati/auth"
import usersService from "@irati/identity/services/users"

import {Select} from "@irati/ui/src/FormItem"
import {adminDashboardTracking} from "@irati-tracking/admin"
import {WidgetFilterType} from "@irati-tracking/admin/adminDashboard"
import Region, {ALL_TEAMS} from "../../Reporting/Region"
import {
  CompanyBenefitsWidget,
  CostOfLivingWidget,
  HighestSavingPerksWidget,
  RecognitionWidget,
  ValuesCelebrationsWidget,
  WellnessWidget
} from "./Widgets"
import EmptyCelebrations from "./Widgets/Recognition/EmptyCelebrations"
import EmptyValues from "./Widgets/ValuesAndCelebrations/EmptyValues"
import MaximiseUsage from "./Widgets/MaximiseUsage"

interface Props extends InjectIntl {}

const Onboarding: FC<Props> = ({formatMessage}) => {
  const timeRangeOptions = [
    {
      label: formatMessage({id: "admin.components.dashboard.reporting.timeRangeOptions.last90Days"}),
      value: BusinessOverviewDuration.BusinessOverviewLast_90Days
    },
    {
      label: formatMessage({id: "admin.components.dashboard.reporting.timeRangeOptions.last12Months"}),
      value: BusinessOverviewDuration.BusinessOverviewLast_12Months
    }
  ]

  const [teamId, setTeamId] = useState<string>(ALL_TEAMS)
  const [duration, setDuration] = useState<string>(BusinessOverviewDuration.BusinessOverviewLast_12Months)
  const [currency, setCurrency] = useState<string>(PaymentCurrencyType.Gbp)
  const [overview, setOverview] = useState<any>(null)
  const [costOfLiving, setCostOfLiving] = useState<any>(null)
  const [recognition, setRecognition] = useState<any>(null)
  const [showCelebrationWidgets, setShowCelebrationWidgets] = useState<boolean>(true)
  const [showValuesWidget, setShowValuesWidget] = useState<boolean>(true)
  const [valuesAndCelebrations, setValuesAndCelebrations] = useState<any>(null)
  const [topCompanyBenefits, setTopCompanyBenefits] = useState<any>(null)
  const [highestSavingPerks, setHighestSavingPerks] = useState<any>(null)
  const [wellness, setWellness] = useState<any>(null)
  const [load, setLoad] = useState<Promise<any> | null>(null)
  const [showMaximiseUsageWidget, setShowMaximiseUsageWidget] = useState(false)

  useEffect(() => {
    adminDashboardTracking.adminDashboardWidgets()
    setLoad(getBusinessOverview(true))
    usersService.regionIdForUser(authentication.user().uuid!).then(({regionId}) => {
      if (regionId === "AU" || regionId === "NZ") {
        setCurrency(PaymentCurrencyType.Aud)
      }
    })
  }, [])

  useEffect(() => {
    setLoad(getBusinessOverview())
  }, [teamId, duration, currency])

  const getBusinessOverview = (isFirstLoad?: boolean) => {
    const businessId = authentication.business().uuid
    return Promise.all([
      adminReportingService.getBusinessSettings(businessId),
      adminReportingService.getBusinessOverview({businessId, duration, teamId: teamId !== ALL_TEAMS ? teamId : undefined})
    ]).then(([settings, businessOverview]) => {
      const isOverrideActive = settings?.showUntil?.seconds && moment().isBefore(moment.unix(settings.showUntil.seconds))
      const showMaximiseUsage = businessOverview?.showMaximiseUsage && !isOverrideActive

      if (isFirstLoad && showMaximiseUsage) {
        setShowMaximiseUsageWidget(true)
      } else {
        setOverview(businessOverview.overview)
        setCostOfLiving(businessOverview.costOfLiving)
        setHighestSavingPerks(businessOverview.perks)
        setWellness(businessOverview.wellness)
      }
      setTopCompanyBenefits(businessOverview.benefits)
      if (!authentication.apps("recognition") || (isFirstLoad && !businessOverview.recognition?.totalRecognitions)) {
        return setShowCelebrationWidgets(false)
      }
      setRecognition(businessOverview.recognition)
      if (
        isFirstLoad &&
        businessOverview.valuesAndCelebrations?.topValues?.length === 0 &&
        !businessOverview.valuesAndCelebrations?.topCelebration
      ) {
        return setShowValuesWidget(false)
      }
      setValuesAndCelebrations(businessOverview.valuesAndCelebrations)
    })
  }

  const adminDashboardFilterEvent = (action: string) => {
    const isTeamFiltered = teamId !== ALL_TEAMS
    const isDurationFiltered = duration !== BusinessOverviewDuration.BusinessOverviewLast_12Months
    const isCurrencyFiltered = currency !== PaymentCurrencyType.Gbp
    const filter = []
    if (isTeamFiltered) {
      filter.push(WidgetFilterType.AdminWidgetCountry)
    }
    if (isDurationFiltered) {
      filter.push(WidgetFilterType.AdminWidgetDate)
    }
    if (isCurrencyFiltered) {
      filter.push(WidgetFilterType.AdminWidgetCurrency)
    }
    const isFiltered = isTeamFiltered || isDurationFiltered || isCurrencyFiltered
    adminDashboardTracking.adminDashboardWidgets(isFiltered, filter, action)
  }

  const handleUpdateTeam = (updatedTeamId: string) => {
    setTeamId(updatedTeamId)
    adminDashboardFilterEvent("filter_team")
  }

  const handleSetTimeRangeUpdate = (updatedDuration: string) => {
    setDuration(updatedDuration)
    adminDashboardFilterEvent("filter_time")
  }

  const handleUpdateCurrency = (currencyType: PaymentCurrencyType) => {
    setCurrency(currencyType)
    adminDashboardFilterEvent("filter_currency")
  }

  return (
    <>
      <H2 color={theme.colors.filterChip}>
        <FormattedMessage id="admin.components.dashboard.reporting.title" />
      </H2>
      <Flex justifyContent="space-between" alignItems="center" wrap="wrap">
        <Body maxWidth="none">
          <FormattedMessage id="admin.components.dashboard.reporting.body" />
        </Body>
        <Flex alignItems="center" gap={1} justifyContent="flex-end">
          <Region id="adminDashboard_reporting_country" value={teamId} onChange={handleUpdateTeam} />
          <Row>
            <Select
              id="adminDashboard_reporting_timePeriod"
              name="adminDashboardReportingTimePeriod"
              aria-label="Select Time Period"
              options={timeRangeOptions}
              value={duration}
              onChange={handleSetTimeRangeUpdate}
            />
          </Row>
          <Row>
            <Select
              id="adminDashboard_reporting_currency"
              aria-label="Select Currency"
              isClearable={false}
              value={currency}
              options={[
                {value: PaymentCurrencyType.Gbp, label: PaymentCurrencyType.Gbp},
                {value: PaymentCurrencyType.Aud, label: PaymentCurrencyType.Aud}
              ]}
              onChange={handleUpdateCurrency}
            />
          </Row>
        </Flex>
      </Flex>
      <Loading load={load}>
        <Flex marginBottom={2} gap={2} wrap="wrap">
          {showMaximiseUsageWidget ? (
            <>
              <MaximiseUsage />
              <CompanyBenefitsWidget topCompanyBenefits={topCompanyBenefits} />
            </>
          ) : (
            <>
              <CostOfLivingWidget costOfLiving={costOfLiving} currencyCode={currency} overview={overview} />
              {showCelebrationWidgets ? (
                <>
                  <RecognitionWidget recognition={recognition} />
                  {showValuesWidget ? <ValuesCelebrationsWidget valuesAndCelebrations={valuesAndCelebrations} /> : <EmptyValues />}
                </>
              ) : (
                <EmptyCelebrations />
              )}
              <HighestSavingPerksWidget highestSavingPerks={highestSavingPerks} currencyCode={currency} />
              <CompanyBenefitsWidget topCompanyBenefits={topCompanyBenefits} />
              <WellnessWidget wellness={wellness} />
            </>
          )}
        </Flex>
      </Loading>
    </>
  )
}

export default injectIntl(Onboarding)
